import { v4 as uuid } from 'uuid';
import { Injectable } from '@angular/core';

import { DprProgressWithoutTargetGridData } from '@dpr-details/models/grid';
import { DprProgressWithoutTargetDetail } from '@shared/models/dpr';
import { Sync } from '@shared/models/grid';
import { SyncService } from '@shared/services/grid';
import { ProgressWithoutTargetItem } from '@shared/models/project-setup';

@Injectable()
export class DprProgressWithoutTargetGridService {
  constructor(private syncService: SyncService) {}

  buildGridData(
    dprProgressWithoutTargetItems: DprProgressWithoutTargetDetail[],
    progressWithoutTargetItems: ProgressWithoutTargetItem[]
  ): DprProgressWithoutTargetGridData[] {
    const activeProgressItems = progressWithoutTargetItems.filter(
      (x) => x.isActive
    );

    return dprProgressWithoutTargetItems.reduce(
      (result, dprProgressWithoutTargetItem) => {
        const progressWithoutTargetItem = activeProgressItems.find(
          (x) => x.id === dprProgressWithoutTargetItem.progressWithoutTargetId
        );

        return progressWithoutTargetItem
          ? [
              ...result,
              {
                id: dprProgressWithoutTargetItem.id ?? uuid(),
                priority: progressWithoutTargetItem.priority,
                progressWithoutTargetId: progressWithoutTargetItem.id,
                isMandatory: progressWithoutTargetItem.isMandatory,
                clearedToday: dprProgressWithoutTargetItem?.clearedToday,
                clearedTotal: dprProgressWithoutTargetItem.clearedTotal,
                clearedTotalInitialValue:
                  (dprProgressWithoutTargetItem.clearedTotal ||
                    progressWithoutTargetItem?.initialValue ||
                    0) - (dprProgressWithoutTargetItem?.clearedToday || 0),
                comment: dprProgressWithoutTargetItem.comment,
                name: progressWithoutTargetItem.name,
              },
            ]
          : result;
      },
      []
    );
  }

  mapToGridSync(
    changes: Sync<DprProgressWithoutTargetGridData>,
    dprProgressWithoutTargetId: number | string
  ): Sync<DprProgressWithoutTargetDetail> {
    const result =
      this.syncService.getDefaultSyncModel<DprProgressWithoutTargetDetail>();

    const mappingFunction = (data: DprProgressWithoutTargetGridData) => ({
      id: data.id,
      dprProgressWithoutTargetId,
      progressWithoutTargetId: data.progressWithoutTargetId,
      clearedToday: data.clearedToday,
      comment: data.comment,
      clearedTotal: data.clearedTotal,
    });

    result.addedList.push(...(changes?.addedList?.map(mappingFunction) ?? []));
    result.addedList.push(
      ...(changes?.updatedList
        ?.filter((x) => isNaN(+x.id))
        .map(mappingFunction) ?? [])
    );

    result.updatedList = changes?.updatedList
      ?.filter((x) => !isNaN(+x.id))
      .map(mappingFunction);

    return result;
  }

  shouldAutoFill(data: DprProgressWithoutTargetGridData[]): boolean {
    if (!data.length) {
      return false;
    }

    const someItemsFilled = data.some((x) => x.clearedToday != null);
    const someItemsEmpty = data.some((x) => x.clearedToday == null);

    return someItemsEmpty && someItemsFilled;
  }
}
